import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { legalAPI } from '../lib/api';
import { toast } from 'sonner';

/**
 * Vraagt opnieuw akkoord zodra een juridisch document een nieuwe versie heeft.
 *
 * Bewust blokkerend: onder de AVG moet aantoonbaar zijn dat iemand de geldende
 * versie aanvaard heeft. Wie doorwerkt zonder te bevestigen, laat een gat in
 * dat bewijs achter. Wegklikken kan dus niet -- uitloggen wel.
 */
export const LegalGate = ({ onLogout }) => {
  const [pending, setPending] = useState([]);
  const [laden, setLaden] = useState(true);
  const [bezig, setBezig] = useState(false);
  const [aangevinkt, setAangevinkt] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await legalAPI.getPending();
        setPending(data.pending || []);
      } catch (e) {
        // Niet blokkeren als deze controle zelf faalt: dan is de gebruiker
        // onbereikbaar door een storing die niets met de voorwaarden te maken heeft.
        setPending([]);
      } finally {
        setLaden(false);
      }
    })();
  }, []);

  if (laden || pending.length === 0) return null;

  const bevestig = async () => {
    setBezig(true);
    try {
      await legalAPI.accept();
      toast.success('Bedankt, je akkoord is vastgelegd.');
      setPending([]);
    } catch (e) {
      toast.error('Vastleggen mislukt. Probeer het opnieuw.');
    } finally {
      setBezig(false);
    }
  };

  const nieuw = pending.some((p) => p.previously_accepted);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
         data-testid="legal-gate">
      <div className="bg-white rounded-2xl max-w-lg w-full p-7 shadow-xl">
        <div className="flex gap-3 mb-4">
          <ShieldAlert className="w-6 h-6 text-[#1A4D2E] flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-heading text-xl font-bold text-gray-900">
              {nieuw ? 'Onze voorwaarden zijn gewijzigd' : 'Nog even akkoord geven'}
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              {nieuw
                ? 'Er is een nieuwe versie van onderstaande documenten. Lees ze door en bevestig om verder te gaan.'
                : 'Bevestig onderstaande documenten om verder te gaan.'}
            </p>
          </div>
        </div>

        <ul className="border border-gray-200 rounded-xl divide-y divide-gray-200 mb-5">
          {pending.map((p) => (
            <li key={p.doc_type} className="flex items-center justify-between gap-3 px-4 py-3">
              <div>
                <Link to={p.url} target="_blank"
                      className="text-[#1A4D2E] font-medium hover:underline">
                  {p.title}
                </Link>
                <p className="text-xs text-gray-500">
                  Versie {p.version}
                  {p.previously_accepted ? ` · je ging eerder akkoord met ${p.previously_accepted}` : ''}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <label className="flex items-start gap-3 cursor-pointer mb-5">
          <input type="checkbox" checked={aangevinkt}
                 onChange={(e) => setAangevinkt(e.target.checked)}
                 data-testid="legal-gate-checkbox"
                 className="mt-1 w-4 h-4 rounded border-gray-300 text-[#1A4D2E]" />
          <span className="text-sm text-gray-700">
            Ik heb de bovenstaande documenten gelezen en ga ermee akkoord.
          </span>
        </label>

        <div className="flex gap-3">
          <Button onClick={bevestig} disabled={!aangevinkt || bezig}
                  data-testid="legal-gate-accept" className="btn-primary flex-1 h-11">
            {bezig ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Akkoord en verdergaan'}
          </Button>
          <Button variant="outline" onClick={onLogout} className="h-11">
            Uitloggen
          </Button>
        </div>
      </div>
    </div>
  );
};
